import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { getListAsync, innerScrollEl, ITEMS_LIMIT, returnScrollPosition, scrollToSelected } from 'src/app/app.utils';
import { HelperProvider } from 'src/app/providers/helper/helper';
import { Label } from 'src/app/model';
import { DmStoreService } from 'src/app/store/dm-store.service';
import { AlertController, IonicModule } from '@ionic/angular';
import { ApiService } from 'src/app/providers/api/api.service';
import { Subscription } from 'rxjs';
import { IonicSelectableComponent, IonicSelectableFooterTemplateDirective, IonicSelectableIconTemplateDirective, IonicSelectableItemTemplateDirective, IonicSelectablePlaceholderTemplateDirective, IonicSelectableValueTemplateDirective } from 'ionic-selectable';
import { UserProfile } from 'src/app/providers';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-labels-select',
  templateUrl: './labels-select.component.html',
  styleUrls: ['./labels-select.component.scss'],
  standalone: true,
  imports: [IonicModule, FormsModule, CommonModule, IonicSelectableComponent, IonicSelectableFooterTemplateDirective, IonicSelectablePlaceholderTemplateDirective, IonicSelectableIconTemplateDirective, IonicSelectableItemTemplateDirective, IonicSelectableValueTemplateDirective],
})
export class LabelsSelectComponent implements OnInit, OnDestroy {
  private labelsSub: Subscription;
  private selectedZonesSub: Subscription;

  private page = 1;

  public labels$ = this.dmStore.labels$;
  public selectedZone$ = this.dmStore.selectedZone$;

  public selectedLabels: number[] = [];

  @ViewChild('labelsSelectComponent') labelsSelectComponent: IonicSelectableComponent;

  @Input() disabled: boolean;

  constructor(
    private dmStore: DmStoreService,
    private alertController: AlertController,
    private api: ApiService,
    private helper: HelperProvider,
    private userProfile: UserProfile,
  ) { }

  async ngOnInit() {
    await this.dmStore.fetchLabels();
    this.selectedZonesSub = this.dmStore.selectedZone$.subscribe(() => {
      this.selectedLabels = [];
    });
    this.labelsSub = this.dmStore.selectedLabels$.subscribe(labels => {
      this.selectedLabels = labels.map(l => l.id);
    });
  }

  ngOnDestroy() {
    this.labelsSub?.unsubscribe();
    this.selectedZonesSub?.unsubscribe();
  }

  public selectLabels() {
    this.labelsSelectComponent.confirm();
    this.labelsSelectComponent.close();

    this.dmStore.preselectedValuesClear();

    this.dmStore.selectLabels(this.selectedLabels);
  }

  public async createLabel() {
    this.labelsSelectComponent.close();

    const groupnameAlert = await this.alertController.create({
      header: 'Label name',
      inputs: [
        {
          name: 'name',
          placeholder: 'Name',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Create',
          handler: ({ name }: { name: string }) => {
            if (name.trim()?.length) {
              this.labelCreate(name);
            }

            groupnameAlert.dismiss();
            return false;
          },
        },
      ],
    });

    groupnameAlert.present();
  }

  private async labelCreate(label: string) {
    this.helper.showLoading();

    const isAdmin = await this.userProfile.isAdmin();

    const labelData = await this.api.labels.create({
      label,
      ...(isAdmin && { owner_id: this.dmStore.selectedZone.value?.owner_id }),
    }, this.helper);

    this.helper.hideLoading();

    if (labelData) {
      await this.dmStore.fetchLabels();
      this.dmStore.selectLabels([labelData.id]);
    }
  }

  public filterLabels(labels: Label[], text: string) {
    return labels.filter(label => {
      return label.label.toLowerCase().includes(text);
    });
  }

  public getMoreLabels(event: {
    component: IonicSelectableComponent,
    text: string
  }) {
    const scrollEl = innerScrollEl();
    const scrollVal = scrollEl?.scrollTop || 0;

    if (!scrollEl) {
      return;
    }

    const text = (event.text || '').trim().toLowerCase();

    if (this.page >= Math.ceil(this.dmStore.labels.length / ITEMS_LIMIT)) {
      event.component.disableInfiniteScroll();
      return;
    }

    getListAsync<Label>(this.dmStore.labels, this.page, ITEMS_LIMIT).subscribe(labels => {
      labels = event.component.items.concat(labels);

      if (text) {
        labels = this.filterLabels(labels, text);
      }

      event.component.items = labels;
      event.component.endInfiniteScroll();
      this.page++;

      if (!text) {
        returnScrollPosition(scrollEl, scrollVal);
      }
    });
  }

  public searchLabels(event: {
    component: IonicSelectableComponent,
    text: string
  }) {
    const text = event.text.trim().toLowerCase();
    event.component.startSearch();

    if (!text) {
      event.component.items = this.dmStore.labels.slice(0, this.page * ITEMS_LIMIT);
      event.component.endSearch();
      scrollToSelected();
      return;
    }

    event.component.items = this.filterLabels(this.dmStore.labels, text);
    event.component.enableInfiniteScroll();
    event.component.endSearch();
  }


  public getLabelByID(labelId: number): string {
    const currentLabel = this.dmStore.labels.find(lb => lb.id === labelId);

    return currentLabel?.label || '';
  }

  public getIconForLabelByID(labelId: number): string {
    const label = this.dmStore.labels.find(lb => lb.id === labelId);
    if (label) {
      return this.getIconForLabel(label.label);
    }

    return '';
  }

  public getIconForLabel(label: string): string {
    const lowerCaseLabel = label.toLowerCase();

    switch (true) {
      case lowerCaseLabel.includes('disabled'):
        return 'assets/custom-icons/disabled-person.svg';
      case lowerCaseLabel.includes('vip'):
        return 'assets/custom-icons/vip.svg';
      case lowerCaseLabel.includes('ev'):
        return 'assets/custom-icons/power.svg';
      default:
        return 'assets/custom-icons/tag.svg';
    }
  }

}
